import { Component, computed } from '@angular/core';
import { ApiGatewayService } from 'src/app/services/api-gateway.service';
import { emitInfoMessage, InfoMessageService } from 'src/app/services/info-message.service';
import { activeSubscriptions, formatMoney, nextBand, SubscriptionOrderService } from 'src/app/services/subscription-order.service';

function sizes(amounts: number[]): string {
  const list = [...new Set(amounts)].sort((a, b) => a - b).map(amount => amount.toLocaleString('en-GB'));
  return list.length > 1 ? list.slice(0, -1).join(', ') + ' or ' + list[list.length - 1] : list.join('');
}

@Component({
    selector: 'tab-subscriptions-fleet',
    templateUrl: './tab-subscriptions-fleet.component.html',
    standalone: false
})
export class TabSubscriptionsFleetComponent {
  today?: { places: number, unlimited: boolean, inUse: number, hasPlan: boolean };
  money = formatMoney;
  nextBand = nextBand;
  readonly terms = [{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Yearly' }] as const;
  readonly volumeFrom = computed(() => nextBand(this.order.node(), 1)?.band_min_quantity);

  readonly subtitle = computed(() => {
    const fleet = this.order.products().filter(p => p.type === 'CONNECTIVITY_FLEET');
    return `Connectivity for fleets: plans of ${sizes(fleet.filter(p => !p.required_product_uuid).map(p => p.amount))} nodes, `
      + `growing in blocks of ${sizes(fleet.filter(p => p.required_product_uuid).map(p => p.amount))}`;
  });

  constructor(
    private gatewayApiService: ApiGatewayService,
    private infoMessageService: InfoMessageService,
    public order: SubscriptionOrderService,
  ) {
    this.getPrices();
    this.getToday();
  }

  async getPrices() {
    try {
      await this.order.loadPriceList();
    } catch (error: any) {
      emitInfoMessage("Could not get prices: " + error?.message, this.infoMessageService);
    }
  }

  async getToday() {
    try {
      const active = activeSubscriptions((await this.gatewayApiService.getSubscriptions()).body ?? []);
      const profile = (await this.gatewayApiService.getUserProfile()).body;
      this.today = {
        places: active.reduce((sum, s) => sum + s.capacity_devices, 0),
        unlimited: active.some(s => s.capacity_devices === 0),
        inUse: profile?.organization?.device_count ?? 0,
        hasPlan: active.some(s => s.mode === 'SUBSCRIPTION'),
      };
    } catch (error: any) {
      emitInfoMessage("Could not get subscriptions: " + error?.message, this.infoMessageService);
    }
  }

  get summary(): string {
    const size = this.order.fleetSize();
    const nodes = this.order.nodeLine();
    return [
      ...(size ? [`Fleet plan for ${size.toLocaleString('en-GB')} nodes`, this.order.monthly() ? 'monthly' : 'yearly'] : []),
      ...(nodes ? [`${nodes.quantity.toLocaleString('en-GB')} ${nodes.name}${nodes.quantity === 1 ? '' : 's'}`] : []),
    ].join(' · ') || 'Your order is empty';
  }

  get onRequest(): boolean {
    return this.order.fleetLines().some(line => line.totalCents === undefined);
  }

  setSize(size: number) {
    this.order.fleetSize.set(Math.max(0, Math.floor(size) || 0));
  }

  sizeDown() {
    this.setSize(Math.ceil(this.order.fleetSize() / 50) * 50 - 50);
  }

  sizeUp() {
    this.setSize(Math.floor(this.order.fleetSize() / 50) * 50 + 50);
  }

  setNodes(quantity: number) {
    this.order.nodeQuantity.set(Math.max(1, Math.floor(quantity) || 1));
  }
}
